// frontend/src/pages/player/EventRegistration.js
import React, { useEffect, useState, useContext } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import { resolveAssetUrl } from "../../api/config";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";

const isRelayGame = (game) => {
  const name = String(game?.game_name || "").toLowerCase();
  const narration = String(game?.game_narration || "").toLowerCase();
  return /(relay|4x)/.test(`${name} ${narration}`);
};

const formatEventDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString();
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatEventDate(end);
  if (!end || end === start) return formatEventDate(start);
  return `${formatEventDate(start)} to ${formatEventDate(end)}`;
};

const normalizeClassificationToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const requiresGuideRunnerForClassification = (classification) => {
  const token = normalizeClassificationToken(classification);
  if (!token) return false;
  if (/^(B|B1|B2|B3)$/.test(token)) return true;
  if (/^(T1[123]|F1[123]|S1[123]|SB1[123]|SM1[123]|PTVI|VI|ARVI)$/.test(token)) return true;
  if (token.includes("BLIND") || token.includes("VISUAL")) return true;
  return false;
};

const isGuideRunnerType = (type) => {
  const token = String(type || "").trim().toLowerCase();
  return token.includes("guide");
};

const formatAgeCategoryLabel = (value) => {
  const token = String(value || "").toLowerCase();
  if (token === "sub_junior") return "Sub-Junior";
  if (token === "junior") return "Junior";
  if (token === "senior") return "Senior";
  return token ? token : "Unknown";
};

const EventRegistration = () => {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [popupData, setPopupData] = useState({});
  // escorts: support up to 2 escorts, optional
  const [escorts, setEscorts] = useState([
    { escort_id: "", escort_name: "", escort_email: "", escort_type: "", escort_photo: null },
    { escort_id: "", escort_name: "", escort_email: "", escort_type: "", escort_photo: null },
  ]);
  const [playerPhoto, setPlayerPhoto] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [appliedEvents, setAppliedEvents] = useState([]);
  const [showSecondEscort, setShowSecondEscort] = useState(false);
  const [eligibilityContext, setEligibilityContext] = useState({
    gender: "",
    age_category: "",
    classification: "",
  });
  const maxGamesAllowed = Number(popupData.max_games_per_player || 3);
  const guideRunnerRequired = requiresGuideRunnerForClassification(popupData.classification);

  const getEscortId = (slot) => `ESCORT${String(user?.id || 0).padStart(5, "0")}-${slot}`;

  useEffect(() => {
    if (!user || !user.id) return;

    setLoading(true);

    API.fetchOpenEvents()
      .then((res) => {
        console.log("OPEN EVENTS API RESPONSE:", res);
        const list = res?.data ?? [];
        setEvents(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("Error loading open events:", err);
        setError(err?.message || "Unable to load events");
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (!message) return;
    successAlert("Registration submitted", message);
    setMessage("");
  }, [message]);

  useEffect(() => {
    if (!error) return;
    if (String(error).toLowerCase().includes("maximum")) {
      warningAlert("Selection limit", error);
    } else {
      errorAlert("Registration issue", error);
    }
    setError("");
  }, [error]);

  useEffect(() => {
    if (!user || !user.id) return;
    API.fetchPlayerRegistrations(user.id)
      .then((data) => setAppliedEvents((data || []).map((r) => r.event_id)))
      .catch((err) => console.error("Error loading player registrations:", err));
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const loadPhoto = async () => {
      try {
        const res = await API.fetchProfile();

        if (res?.photo) {
          setPlayerPhoto(
            resolveAssetUrl(res.photo, {
              folder: "players",
              entityId: user?.id,
            })
          );
        }
      } catch (err) {
        console.warn("No player photo found");
      }
    };

    loadPhoto();
  }, [user]);

  // When selecting event, also grab escort_fee if available
  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    try {
      const [gamesRes, classRes] = await Promise.all([
        API.fetchGamesBySport(event.sport_id, { player_id: user.id }),
        API.fetchPlayerClassification(user.id, event.sport_id),
      ]);
      const allGames = gamesRes?.data ?? gamesRes;
      const list = Array.isArray(allGames) ? allGames : [];
      const nonRelayGames = list.filter((g) => !isRelayGame(g));
      setGames(nonRelayGames);
      const effectiveFee = Number(event.registration_fee || 0);
      const baseFee = Number(
        typeof event.base_registration_fee !== "undefined"
          ? event.base_registration_fee
          : event.registration_fee || 0
      );
      const effectiveEscortFee = Number(event.escort_fee || 0);
      const baseEscortFee = Number(
        typeof event.base_escort_fee !== "undefined"
          ? event.base_escort_fee
          : event.escort_fee || 0
      );
      const classificationValue =
        (classRes?.classification || classRes?.data?.classification) || "Not Classified";
      const guideRequired = requiresGuideRunnerForClassification(classificationValue);
      const firstEligibility = nonRelayGames.find((g) => g?.eligibility)?.eligibility || null;
      setEligibilityContext({
        gender: firstEligibility?.player_gender || "",
        age_category: firstEligibility?.player_age_category || "",
        classification: firstEligibility?.player_classification || classificationValue,
      });
      setPopupData({
        event_id: event.id,
        event_name: event.title,
        event_date: formatEventDateRange(event.date, event.event_end_date),
        registration_fee: effectiveFee,
        base_registration_fee: baseFee,
        escort_fee: effectiveEscortFee,
        base_escort_fee: baseEscortFee,
        is_fee_exempt: !!event.is_fee_exempt,
        max_games_per_player: event.max_games_per_player || 3,
        classification: classificationValue,
        guide_runner_required: guideRequired,
      });

      // create auto escort ids for both escort slots
      setEscorts((prev) => {
        const copy = [...prev];
        copy[0] = {
          ...copy[0],
          escort_id: getEscortId(1),
          escort_name: "",
          escort_email: "",
          escort_type: guideRequired ? "Guide Runner" : "",
          escort_photo: null,
        };
        copy[1] = {
          ...copy[1],
          escort_id: getEscortId(2),
          escort_name: "",
          escort_email: "",
          escort_type: "",
          escort_photo: null,
        };
        return copy;
      });
      setShowSecondEscort(false);
      setSelectedGames([]);
      setShowPopup(true);
    } catch (err) {
      console.error("Error loading event details:", err);
    }
  };

  const toggleGameSelection = (game) => {
    const gameId = Number(game?.id || 0);
    const eligibility = game?.eligibility || null;
    if (eligibility?.known && !eligibility?.eligible) {
      setError(eligibility?.reason || "You are not eligible for this game.");
      return;
    }
    setError("");
    setSelectedGames((prev) => {
      if (prev.includes(gameId)) {
        return prev.filter((id) => id !== gameId);
      }
      if (prev.length >= maxGamesAllowed) {
        setError(`You can select a maximum of ${maxGamesAllowed} games.`);
        return prev;
      }
      return [...prev, gameId];
    });
  };

  const handleChange = (e) => {
    const { name, files, value } = e.target;
    setPopupData({ ...popupData, [name]: files ? files[0] : value });
  };

  // escort input handler (name attributes expected as escort_name_1, escort_type_1, escort_photo_1, etc.)
  const handleEscortChange = (index, e) => {
    const { name, value, files } = e.target;
    setEscorts((prev) => {
      const copy = [...prev];
      const field = name.includes("_1") || name.includes("_2") ? name.replace(/_1|_2$/, "") : name;
      copy[index] = { ...copy[index], [field]: files ? files[0] : value };
      return copy;
    });
  };

  const selectedEscortCount = escorts.filter((e) => e.escort_name).length;
  const selectedGuideRunnerCount = escorts.filter(
    (e) => String(e.escort_name || "").trim() && isGuideRunnerType(e.escort_type || "Guide Runner")
  ).length;
  const totalPreview =
    (Number(popupData.registration_fee || 0) + Number(popupData.escort_fee || 0) * selectedEscortCount) || 0;

  // payment proof is mandatory only when payable amount is greater than zero
  const isFormValid =
    selectedGames.length > 0 &&
    (totalPreview <= 0 || popupData.payment_proof) &&
    (!guideRunnerRequired || selectedGuideRunnerCount > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (guideRunnerRequired && selectedGuideRunnerCount <= 0) {
      setError("Blind category requires at least one guide runner. Add guide runner details before submitting.");
      return;
    }
    const formData = new FormData();
    formData.append("player_id", user.id);
    formData.append("event_id", popupData.event_id);
    formData.append("game_ids", JSON.stringify(selectedGames));
    if (popupData.payment_proof) formData.append("payment_proof", popupData.payment_proof);
    if (popupData.payment_mode) formData.append("payment_mode", popupData.payment_mode);
    if (popupData.payment_reference) formData.append("payment_reference", popupData.payment_reference);

    // append escorts if provided
    // backend expects escort_name_1, escort_type_1, escort_id_1, escort_photo_1 (and _2)
    escorts.forEach((esc, idx) => {
      const i = idx + 1;
      if (esc && esc.escort_name) {
        const normalizedEscortType = String(
          esc.escort_type || (guideRunnerRequired ? "Guide Runner" : "")
        ).trim();
        formData.append(`escort_id_${i}`, esc.escort_id || "");
        formData.append(`escort_name_${i}`, esc.escort_name);
        if (esc.escort_email) {
          formData.append(`escort_email_${i}`, esc.escort_email);
        }
        formData.append(`escort_type_${i}`, normalizedEscortType);
        if (esc.escort_photo) {
          // send file as escort_photo_1 or escort_photo_2
          formData.append(`escort_photo_${i}`, esc.escort_photo);
        }
      }
    });

    try {
      const res = await API.registerEvent(formData);
      // ✅ update applied events immediately
      setAppliedEvents((prev) => [
        ...prev,
        popupData.event_id,
      ]);

      setMessage(res.message || "Registration submitted successfully!");
      setShowPopup(false);
    } catch (err) {
      console.error("FULL ERROR:", err);
      console.error("ERRORS:", err?.errors);
      setError(
        err?.message ||
        JSON.stringify(err?.errors) ||
        "Registration failed"
      );
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex flex-col items-center justify-center h-screen text-blue-700">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
          <p className="text-lg font-medium">Loading available events...</p>
        </div>
      </>
    );
  }

  if (!loading && Array.isArray(events) && events.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex flex-col items-center justify-center h-screen text-gray-500">
          <img
            src="https://cdn-icons-png.flaticon.com/512/7486/7486769.png"
            alt="no-events"
            className="w-32 mb-4 opacity-70"
          />
          <h2 className="text-xl font-semibold mb-2">No Open Events Found</h2>
          <p className="text-center max-w-md">
            There are currently no open events for registration. Please check back later.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 mt-6">
        <h1 className="text-2xl font-bold text-center text-blue-700 mb-6">
          Event Registration
        </h1>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.isArray(events) && events.length > 0 ? (
            events.map((event) => (
              <div
                key={event.id}
                className={`p-4 border rounded-xl shadow-sm transition ${
                  selectedEvent?.id === event.id ? "border-blue-500 bg-blue-50" : "border-gray-200"
                }`}
              >
                <h3 className="font-semibold text-lg">{event.title}</h3>
                <p className="text-sm text-gray-600">Date: {formatEventDateRange(event.date, event.event_end_date)}</p>
                <p className="text-sm text-gray-600">Fee: ₹{event.registration_fee}</p>
                {event.is_fee_exempt && (
                  <p className="text-xs font-semibold text-emerald-700">
                    Registration and escort fee exemption applied for your account
                  </p>
                )}
                {typeof event.escort_fee !== "undefined" && (
                  <p className="text-sm text-gray-600">Escort fee (per escort): ₹{event.escort_fee}</p>
                )}
                <p className="text-sm text-gray-600">Sport: {event.sport_name}</p>
                <button
                  onClick={() => handleSelectEvent(event)}
                  disabled={appliedEvents.includes(event.id)}
                  className={`mt-3 w-full text-white py-1 rounded transition ${
                    appliedEvents.includes(event.id)
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {appliedEvents.includes(event.id)
                    ? "Already Registered"
                    : "Register Now"}
                </button>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">No open events available.</p>
          )}
        </div>

        {/* Popup */}
        {showPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-xl p-6 w-[90%] md:w-[600px] relative shadow-lg overflow-y-auto max-h-[90vh]">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-red-600"
                onClick={() => setShowPopup(false)}
              >
                ✕
              </button>

              <h2 className="text-xl font-semibold text-center mb-4 text-blue-700">
                Register for {popupData.event_name}
              </h2>

              {playerPhoto && (
                <div className="flex justify-center mb-3">
                  <img
                    src={playerPhoto}
                    alt="Player"
                    className="w-20 h-20 rounded-full border-2 border-blue-500 object-cover"
                  />
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3">
                <input type="text" value={popupData.event_date} readOnly className="w-full border p-2 rounded bg-gray-100" />

                <div>
                  <label className="block font-medium">Select up to {maxGamesAllowed} Games</label>
                  <p className="text-xs text-gray-600 mb-1">
                    Category: {formatAgeCategoryLabel(eligibilityContext.age_category)} | Gender:{" "}
                    {eligibilityContext.gender ? eligibilityContext.gender[0].toUpperCase() + eligibilityContext.gender.slice(1) : "Unknown"} | Class:{" "}
                    {eligibilityContext.classification || popupData.classification || "Not Classified"}
                  </p>
                  <div className="border rounded p-2 max-h-48 overflow-y-auto">
                    {games.map((g) => (
                      <label
                        key={g.id}
                        className={`flex items-start space-x-2 text-sm mb-1 p-1 rounded ${
                          g?.eligibility?.known && !g?.eligibility?.eligible ? "bg-rose-50" : "bg-white"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGames.includes(Number(g.id))}
                          disabled={g?.eligibility?.known && !g?.eligibility?.eligible}
                          onChange={() => toggleGameSelection(g)}
                        />
                        <span className="flex-1">
                          <span className="font-medium">{g.game_name}</span>
                          {g?.eligibility?.known ? (
                            <span
                              className={`ml-2 text-[11px] px-2 py-0.5 rounded ${
                                g?.eligibility?.eligible
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {g?.eligibility?.eligible ? "Eligible" : "Not Eligible"}
                            </span>
                          ) : (
                            <span className="ml-2 text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                              Rule Not Mapped
                            </span>
                          )}
                          {g?.eligibility?.eligible_classes ? (
                            <div className="text-[11px] text-gray-500 mt-0.5">
                              Eligible classes: {g.eligibility.eligible_classes}
                            </div>
                          ) : null}
                          {g?.eligibility?.known && !g?.eligibility?.eligible && g?.eligibility?.reason ? (
                            <div className="text-[11px] text-rose-700 mt-0.5">{g.eligibility.reason}</div>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <input type="text" value={popupData.classification} readOnly className="w-full border p-2 rounded bg-gray-100" />
                {guideRunnerRequired && (
                  <p className="text-xs text-amber-700 mt-1">
                    Blind category detected. Add at least one <strong>Guide Runner</strong>. Heat allocation will use
                    player+guide lane pairing (8 lanes = 4 players).
                  </p>
                )}

                <div>
                  <label className="block font-medium">
                    Payment Proof {totalPreview > 0 ? "(Required)" : "(Optional)"}
                  </label>
                  {popupData.is_fee_exempt && Number(popupData.registration_fee || 0) === 0 && (
                    <p className="text-xs text-emerald-700 mb-1">
                      Registration and escort fee exemption is active. Upload proof only if you have payable charges.
                    </p>
                  )}
                  <input type="file" name="payment_proof" accept="image/*,.pdf" onChange={handleChange} className="w-full border p-2 rounded" />
                </div>

                {/* Escort(s) */}
                <h3 className="font-semibold mt-4 text-blue-600">
                  {guideRunnerRequired ? "Guide Runner Information (required for blind category)" : "Escort Information (optional)"}
                </h3>

                {/* Escort 1 */}
                <div className="p-2 border rounded">
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner ID" : "Escort ID"}</label>
                    <input type="text" name="escort_id_1" value={escorts[0].escort_id} readOnly className="w-full border p-2 rounded bg-gray-100" />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Name" : "Escort Name"}</label>
                    <input
                      type="text"
                      name="escort_name_1"
                      placeholder={guideRunnerRequired ? "Guide Runner Name" : "Escort Name"}
                      value={escorts[0].escort_name}
                      onChange={(e) => handleEscortChange(0, e)}
                      className="w-full border p-2 rounded mt-1"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Email" : "Escort Email"}</label>
                    <input type="email" name="escort_email_1" placeholder={guideRunnerRequired ? "Guide Runner Email (optional)" : "Escort Email (optional)"} value={escorts[0].escort_email || ""} onChange={(e) => handleEscortChange(0, e)} className="w-full border p-2 rounded mt-1" />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Type" : "Escort Type"}</label>
                    <input
                      type="text"
                      name="escort_type_1"
                      placeholder={guideRunnerRequired ? "Guide Runner" : "Coach / Parent / Helper"}
                      value={escorts[0].escort_type}
                      onChange={(e) => handleEscortChange(0, e)}
                      className="w-full border p-2 rounded mt-1"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Photo" : "Escort Photo"}</label>
                    <input type="file" name="escort_photo_1" accept="image/*" onChange={(e) => handleEscortChange(0, e)} className="w-full border p-2 rounded mt-1" />
                  </div>
                </div>

                {/* Add second escort toggle */}
                {!showSecondEscort && (
                  <div className="text-right">
                    <button type="button" onClick={() => {
                      setShowSecondEscort(true);
                      setEscorts((prev) => {
                        const copy = [...prev];
                        copy[1] = {
                          ...copy[1],
                          escort_id: copy[1].escort_id || getEscortId(2),
                          escort_type: copy[1].escort_type || (guideRunnerRequired ? "Guide Runner" : copy[1].escort_type),
                        };
                        return copy;
                      });
                    }} className="text-sm text-blue-600 underline">
                      {guideRunnerRequired ? "+ Add second guide runner" : "+ Add second escort"}
                    </button>
                  </div>
                )}

                {showSecondEscort && (
                  <div className="p-2 border rounded">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Escort 2</h4>
                      <button type="button" className="text-sm text-red-600" onClick={() => {
                        setShowSecondEscort(false);
                        // clear second escort data
                        setEscorts((prev) => {
                          const copy = [...prev];
                          copy[1] = { escort_id: getEscortId(2), escort_name: "", escort_email: "", escort_type: "", escort_photo: null };
                          return copy;
                        });
                      }}>Remove</button>
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner ID" : "Escort ID"}</label>
                      <input type="text" name="escort_id_2" value={escorts[1].escort_id} readOnly className="w-full border p-2 rounded mt-1 bg-gray-100" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Name" : "Escort Name"}</label>
                      <input type="text" name="escort_name_2" placeholder={guideRunnerRequired ? "Guide Runner 2 Name" : "Escort 2 Name"} value={escorts[1].escort_name} onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Email" : "Escort Email"}</label>
                      <input type="email" name="escort_email_2" placeholder={guideRunnerRequired ? "Guide Runner 2 Email (optional)" : "Escort 2 Email (optional)"} value={escorts[1].escort_email || ""} onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Type" : "Escort Type"}</label>
                      <input type="text" name="escort_type_2" placeholder={guideRunnerRequired ? "Guide Runner" : "Coach / Parent / Helper"} value={escorts[1].escort_type} onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Photo" : "Escort Photo"}</label>
                      <input type="file" name="escort_photo_2" accept="image/*" onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                  </div>
                )}

                {/* Total preview */}
                <div className="text-right text-sm font-medium">
                  <div>
                    Registration fee: ₹{popupData.registration_fee || 0}
                    {popupData.is_fee_exempt && Number(popupData.registration_fee || 0) === 0 ? " (exempted)" : ""}
                  </div>
                  <div>
                    Escort fee per escort: ₹{popupData.escort_fee || 0}
                    {popupData.is_fee_exempt && Number(popupData.escort_fee || 0) === 0 ? " (exempted)" : ""}
                  </div>
                  <div>Selected escorts: {selectedEscortCount}</div>
                  <div className="mt-1">Total (preview): <span className="font-semibold">₹{totalPreview}</span></div>
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid}
                  className={`w-full py-2 rounded text-white transition ${isFormValid ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}`}
                >
                  Submit Registration
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EventRegistration;
